import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {debounce} from 'lodash';
import React, {
  type Key,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useParams} from 'react-router-dom';
import {type InputAction} from 'react-select';

import {
  type SelectedNotebookQuery,
  type SourceNotebookQuery,
  type SourceNotebookQueryHookResult,
  useSelectedNotebookQuery,
  useSourceNotebookQuery,
} from '../../../../generated/graphql';
import {NetworkStatus} from '../../../../state/graphql/apolloWithAbort';
import {
  CreateNotebookOption,
  SourceNotebookOption,
  SourceNotebookSingleValue,
} from './SourceNotebookSelectCustom';

type Notebook = NonNullable<
  NonNullable<SourceNotebookQuery['project']>['notebooks']
>['edges'][number]['node'];

type SelectedNotebook = SelectedNotebookQuery['notebook'];

const SEARCH_NOTEBOOKS_LIMIT = 5;
const SEARCH_DEBOUNCE_MS = 300;

export type SourceNotebookOption = {
  value: string;
  // Custom component for how it looks in the menu
  option: React.JSX.Element;
  // Custom component for how it looks in the selected value
  singleValue: React.JSX.Element;
};

export type GroupedSourceNotebookOption = {
  options: SourceNotebookOption[];
};

export const CREATE_NOTEBOOK_ID = '0';

const CREATE_NOTEBOOK_ITEM: SourceNotebookOption = {
  value: CREATE_NOTEBOOK_ID,
  option: <CreateNotebookOption />,
  singleValue: <CreateNotebookOption />,
};

function mergeNotebookConnectionPages(
  prev: SourceNotebookQuery,
  fetchMoreResult: SourceNotebookQuery
): SourceNotebookQuery {
  const prevConn = prev.project?.notebooks;
  const nextConn = fetchMoreResult.project?.notebooks;
  if (!prevConn || !nextConn || !prev.project) {
    return prev;
  }
  const mergedEdges = [...prevConn.edges, ...nextConn.edges];
  return {
    ...prev,
    project: {
      ...prev.project,
      notebooks: {
        ...nextConn,
        edges: mergedEdges,
      },
    },
  };
}

function convertNotebookToOption({
  author,
  id,
  name,
  lastUpdated,
}: Notebook): SourceNotebookOption {
  return {
    value: id,
    option: (
      <SourceNotebookOption
        author={author}
        name={name}
        lastUpdated={lastUpdated}
      />
    ),
    singleValue: <SourceNotebookSingleValue name={name} />,
  };
}

async function loadMore({
  data,
  fetchMore,
  networkStatus,
  projectName,
  entityName,
  searchVar,
}: {
  data: SourceNotebookQuery | undefined;
  fetchMore: SourceNotebookQueryHookResult['fetchMore'];
  networkStatus: NetworkStatus;
  projectName: string;
  entityName: string;
  searchVar: string | undefined;
}) {
  const conn = data?.project?.notebooks;
  if (
    !projectName ||
    !entityName ||
    !conn?.pageInfo.hasNextPage ||
    !conn.pageInfo.endCursor ||
    networkStatus === NetworkStatus.fetchMore
  ) {
    return;
  }
  await fetchMore({
    variables: {
      projectName,
      entityName,
      search: searchVar,
      after: conn.pageInfo.endCursor,
      first: SEARCH_NOTEBOOKS_LIMIT,
    },
    updateQuery: (prev, {fetchMoreResult}) => {
      if (!fetchMoreResult?.project?.notebooks) {
        return prev;
      }
      return mergeNotebookConnectionPages(prev, fetchMoreResult);
    },
  });
}

function useDebouncedSearch() {
  const [searchVar, setSearchVar] = useState<string | undefined>(undefined);

  const scheduleSearchUpdate = useMemo(
    () =>
      debounce((value: string) => {
        setSearchVar(value.trim() === '' ? undefined : value.trim());
      }, SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => scheduleSearchUpdate.cancel(), [scheduleSearchUpdate]);

  return {
    searchVar,
    scheduleSearchUpdate,
  };
}

export function useNotebookItems(selectedOptionID: Key | null): {
  notebookItems: GroupedSourceNotebookOption[];
  loading: boolean;
  onInputChange: (newValue: string, {action}: {action: InputAction}) => void;
  onMenuScrollToBottom: () => Promise<void>;
  onBlur: () => void;
  sourceNotebook: SelectedNotebook | null | undefined;
} {
  const {projectName, entityName} = useParams<{
    projectName: string;
    entityName: string;
  }>();

  const {searchVar, scheduleSearchUpdate} = useDebouncedSearch();

  const {data, loading, fetchMore, networkStatus} = useSourceNotebookQuery({
    variables: {
      projectName: projectName ?? '',
      entityName: entityName ?? '',
      search: searchVar,
      first: SEARCH_NOTEBOOKS_LIMIT,
    },
    skip: !projectName || !entityName,
    notifyOnNetworkStatusChange: true,
  });

  const {data: selectedNotebookData, loading: selectedNotebookLoading} =
    useSelectedNotebookQuery({
      variables: {
        id: String(selectedOptionID ?? ''),
      },
      skip: selectedOptionID == null || selectedOptionID === CREATE_NOTEBOOK_ID,
    });

  const loadMoreCallback = useCallback(async () => {
    await loadMore({
      data,
      fetchMore,
      networkStatus,
      projectName,
      entityName,
      searchVar,
    });
  }, [data, entityName, fetchMore, networkStatus, projectName, searchVar]);

  const notebookItems: GroupedSourceNotebookOption[] = useMemo(() => {
    const notebooks =
      data?.project?.notebooks.edges
        ?.map(edge => edge?.node)
        .filter(isNotNullOrUndefined) ?? [];
    const selectedNotebook = selectedNotebookData?.notebook;
    const items: SourceNotebookOption[] =
      notebooks.map(convertNotebookToOption) ?? [];

    // If the selected notebook is not in the list, add it to the top of the list
    if (
      selectedNotebook != null &&
      notebooks.find(notebook => notebook.id === selectedNotebook.id) == null
    ) {
      items.unshift(convertNotebookToOption(selectedNotebook));
    }

    // Always show the create notebook item at the top of the list
    return [{options: [CREATE_NOTEBOOK_ITEM]}, {options: items}];
  }, [data, selectedNotebookData]);

  // This is triggered when the user types in the search box
  const onInputChange = useCallback(
    (newValue: string, {action}: {action: InputAction}) => {
      if (action === 'input-change') {
        scheduleSearchUpdate(newValue);
      }
    },
    [scheduleSearchUpdate]
  );

  const onBlur = useCallback(() => {
    scheduleSearchUpdate('');
  }, [scheduleSearchUpdate]);

  return {
    notebookItems,
    loading:
      loading ||
      selectedNotebookLoading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.refetch,
    onInputChange,
    onMenuScrollToBottom: loadMoreCallback,
    onBlur,
    sourceNotebook: selectedNotebookData?.notebook,
  };
}
